

import React, { useState, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as List } from "bootstrap-icons/icons/list.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { toast } from "react-toastify";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { handleError } from "../../components/utils/AuthenticationCheck";
import AxiosApiCall from "../../api/AxiosApiCall";
import ApiServices from "../../api/ApiServices";

const EditCommission = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [discountType, setDiscountType] = useState("percentage"); // same as Create
  const [pricingRules, setPricingRules] = useState([{ key: "", type: "percentage", value: "" }]);
  const [isActive, setIsActive] = useState(true);
  const [commissionId, setCommissionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [branchProductUnitId, setBranchProductUnitId] = useState("");
  const [branchProductUnit, setBranchProductUnit] = useState([]);


  const navigate = useNavigate();
  const location = useLocation();
  const axios = AxiosApiCall();

  // ---------------- Populate form with existing commission data ----------------
  useEffect(() => {

    if (location.state && location.state.commission) {
      const commission = location.state.commission;
      // console.log("i love comm", commission);
      setCommissionId(commission.id);
      setName(commission.name || "");
      setDescription(commission.description || "");
      setIsActive(commission.is_active !== undefined ? commission.is_active : true);
      setBranchProductUnitId(commission.branch_product_unit_id?.toString() || "");
      // setBranchProductUnitId(commission.branch_product_unit_id || "");

      if (commission.discount_percentage && parseFloat(commission.discount_percentage) > 0) {
        setDiscountType("percentage");
        setDiscountPercentage(commission.discount_percentage?.toString() || "");
      } else if (commission.pricing_rules) {
        setDiscountType("pricing");
        let rules = commission.pricing_rules;
        if (typeof rules === "string") {
          try {
            rules = JSON.parse(rules);
          } catch {
            rules = {};
          }
        }
        const parsedRules = Object.entries(rules).map(([key, rule]) => ({
          key,
          type: rule.type || "percentage",
          value: rule.value || ""
        }));
        setPricingRules(parsedRules.length > 0 ? parsedRules : [{ key: "", type: "percentage", value: "" }]);
      }

    } else {
      toast.error("No commission data provided for editing");
      navigate("/admin/commission/list");
    }
  }, [location.state, navigate]);





  // ---------------- fetch product unit-----------
  const fetchBranchesProCat = async () => {
    setLoading(true);
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) throw new Error("No access token found.");

      const response = await axios.post(ApiServices.getAllBranchProductCategory, {}, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        withCredentials: true,
      });

      const resData = response.data?.data || {};
      const productsData = Array.isArray(resData.data) ? resData.data : [];
      setBranchProductUnit(productsData);

    } catch (error) {
      // console.error("Fetch error:", error);
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranchesProCat();
  }, []);


  // ---------------- Handle pricing rules ----------------
  const handlePricingRuleChange = (index, field, value) => {
    const updatedRules = [...pricingRules];
    updatedRules[index][field] = value;
    setPricingRules(updatedRules);
  };

  const addPricingRule = () => {
    setPricingRules([...pricingRules, { key: "", type: "percentage", value: "" }]);
  };

  const removePricingRule = (index) => {
    const updatedRules = pricingRules.filter((_, i) => i !== index);
    setPricingRules(updatedRules);
  };

  // ---------------- Submit ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !description) {
      toast.error("Name and description are required.");
      return;
    }

    if (discountType === "percentage") {
      if (!discountPercentage || isNaN(discountPercentage) || parseFloat(discountPercentage) <= 0) {
        toast.error("Enter a valid discount percentage.");
        return;
      }
    }

    if (discountType === "pricing") {
      for (let rule of pricingRules) {
        if (!rule.key || !rule.value || isNaN(rule.value)) {
          toast.error("Each pricing rule must have a valid key and value.");
          return;
        }
      }
    }

    setLoading(true);

    const pricingRulesObject = pricingRules.reduce((acc, rule) => {
      if (rule.key && rule.value !== "") {
        acc[rule.key] = {
          type: "percentage", // enforce correct type
          value: parseFloat(rule.value),
        };
      }
      return acc;
    }, {});

    const formData = {
      id: commissionId,
      name,
      branch_product_unit_id: parseInt(branchProductUnitId, 10),
      description,
      discount_percentage: discountPercentage ? parseFloat(discountPercentage) : null,
      pricing_rules: pricingRulesObject,
      is_active: isActive,
    };

    // console.log("form data", formData);

    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) throw new Error("No access token found.");

      const response = await axios.put(ApiServices.updatePhysicalProductCom, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        withCredentials: true,
      });

      const { status, message } = response.data;
      if (status === 200 || status === "200") {
        navigate("/admin/commission/list");
        toast.success(message || "Commission updated successfully");
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <MainLayouts>
        <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
          {/* Breadcrumb */}
          <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
            <nav className="bg-gray-200 py-0 px-4 rounded-lg">
              <ol className="list-none p-0 inline-flex mb-2">
                <li className="flex items-center">
                  <Link to="/admin/commission/list" className="text-darkGreen3 pr-2">
                    Physical Product
                  </Link>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <Link to="/admin/commission/list" className="text-darkGreen3 pr-2">
                    Commission
                  </Link>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <span className="text-orange1">Update</span>
                </li>
              </ol>
            </nav>
          </section>

          {/* Form */}
          <section className="gray_background">
            <div className="container px-1 pb-2 mx-auto">
              <div className="lg:w-full bg-white rounded-lg py-3 px-2 shadow-md" style={{ backgroundColor: "#F7F7F7" }}>
                <form onSubmit={handleSubmit}>
                  {/* Name + Discount Type */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div>
                      <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="border border-dGrey1 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                        placeholder="Enter commission name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="branchProductUnitId"
                        className="block mb-2 text-sm font-medium text-gray-900"
                      >
                        Branch Product Unit
                      </label>
                      <select
                        id="branchProductUnitId"
                        value={branchProductUnitId}
                        onChange={(e) => setBranchProductUnitId(e.target.value)}
                        className="bg-gray-50 border border-dGrey1 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                        required
                      >
                        <option value="">-- Select Branch Product Unit --</option>
                        {branchProductUnit.map((unit) => (
                          <option key={unit.id} value={String(unit.id)}>
                            {unit.name.replace(/_/g, " ")}
                          </option>
                        ))}
                      </select>

                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900">
                        Discount Type
                      </label>
                      <select
                        value={discountType}
                        onChange={(e) => setDiscountType(e.target.value)}
                        className="bg-gray-50 border border-dGrey1 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                      >
                        <option value="percentage">Percentage discount</option>
                        <option value="pricing">Pricing discount</option>
                      </select>
                    </div>
                  </div>

                  {/* Conditional Inputs */}
                  {discountType === "percentage" && (
                    <div className="mt-4">
                      <label htmlFor="discountPercentage" className="block mb-2 text-sm font-medium text-gray-900">
                        Discount Percentage
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        id="discountPercentage"
                        value={discountPercentage || 0}
                        // onChange={(e) => setDiscountPercentage(e.target.value)}
                        onChange={(e) => setDiscountPercentage(Math.max(0, parseFloat(e.target.value) || 0))}
                        className="border border-dGrey1 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                        placeholder="Enter discount percentage"
                      />
                    </div>
                  )}

                  {discountType === "pricing" && (
                    <div className="mt-4">
                      <label className="block mb-2 text-sm font-medium text-gray-900">
                        Pricing Rules
                      </label>
                      <div className="space-y-3">
                        {pricingRules.map((rule, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <input
                              type="text"
                              min="0"
                              placeholder="Quantity e.g. 10, 100"
                              value={rule.key}
                              onChange={(e) => handlePricingRuleChange(index, "key", e.target.value)}
                              className="border border-dGrey1 text-gray-900 text-sm rounded-lg block w-1/3 p-2.5"
                            />
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              placeholder="Percentage Value"
                              value={rule.value}
                              onChange={(e) => handlePricingRuleChange(index, "value", e.target.value)}
                              className="border border-dGrey1 text-gray-900 text-sm rounded-lg block w-1/3 p-2.5"
                            />
                            <button
                              type="button"
                              onClick={() => removePricingRule(index)}
                              className="text-lGrey bg-dash3 text-sm px-1 py-1"
                            >
                              - Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addPricingRule}
                          className="mt-2 text-sm text-lGrey bg-darkGreen3 py-1 px-1"
                        >
                          + Add Rule
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div className="mt-4">
                    <label htmlFor="description" className="block mb-2 text-sm font-medium text-gray-900">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="border border-dGrey1 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                      placeholder="Enter commission description"
                    />
                  </div>

                  {/* Active */}
                  <div className="flex items-center mt-4">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm font-medium text-gray-900">
                      Active Commission
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col gap-4 md:flex-row md:justify-end mt-6">
                    <Link
                      to="/admin/commission/list"
                      className="text-dGrey3 py-3 bg-black2 hover:bg-blue-800 font-medium rounded-lg text-sm px-5 flex items-center"
                    >
                      <List className="mr-2" />
                      Back To List
                    </Link>
                    <button
                      type="submit"
                      className={`text-lGrey bg-darkGreen3 py-2 px-6 rounded font-medium ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-indigo-600"
                        }`}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Commission"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </MainLayouts>
    </div>
  );
};

export default EditCommission;
